/**
 * A component that handles speech-to-text conversion for AI prompts.
 * It records audio, uploads it, converts to text, and updates the prompt with the transcription.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.uploadFile - Function to upload the audio file
 * @param {Function} props.getSpeechToTextTranscript - Function to convert speech to text
 * @param {Object} props.recorderRef - Reference to the audio recorder component
 * @param {Function} props.handleAudioProcessingState - Function to update audio processing state
 * @param {Function} props.updateUserPrompt - Function to update the user prompt with transcript
 * @param {Function} props.handleShowAudioRecorder - Function to show/hide the audio recorder
 * @param {string} props.studentProgressReportId - ID of the student progress report
 * @param {Function} props.handleAudioRecordingState - Function to update audio recording state
 * @param {string} props.parentId - Parent ID for file attachment
 * @param {string} props.parentType - Parent type for file attachment
 * @returns {React.ReactElement} A React component that renders an audio recorder
 */

import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

/* internal */
import { uploadFile, getSpeechToTextTranscript } from "modules/Services";

/* relative */
import AudioRecorderSelector from "UIComponents/AudioRecorderSelector";
import { IconButton } from "@toddle-edu/ds-web";
import { MicrophoneOutlined } from "@toddle-edu/ds-icons";

const AISpeechToText = props => {
  const {
    getSpeechToTextTranscript,
    handleAudioProcessingState,
    updateUserPrompt,
    handleShowAudioRecorder,
    studentProgressReportId,
    handleAudioRecordingState,
    testId,
    handleRecording,
    audioState,
    recordButtonRef,
    scrollContainerRef,
    handleExternalUpdates,
    containerElement,
    getButtonPositionRect,
    getVisualiserContainerRect,
    buttonSize,
  } = props;

  const handleTranscription = async ({ attachment }) => {
    const transcriptionPayload = {
      entityId: studentProgressReportId,
      entityType: "PROGRESS_REPORT",
    };
    transcriptionPayload.blobAttachment = attachment;

    const transcript = (await getSpeechToTextTranscript(transcriptionPayload))
      ?.transcript;

    if (transcript) updateUserPrompt({ value: transcript });
    handleAudioProcessingState({ state: false });
    handleShowAudioRecorder({ state: false });
  };

  const onBlobAttachmentRecieved = async blobAttachment => {
    handleAudioProcessingState({ state: true });
    await handleTranscription({ attachment: blobAttachment });
  };

  const buttonComponent = (
    <IconButton
      variant="neutral"
      type="plain"
      size={buttonSize}
      icon={<MicrophoneOutlined />}
      onClick={() => handleAudioRecordingState({ state: true })}
      disabled={audioState.isProcessing || audioState.isRecording}
      testId={`${testId}-AIVoiceRecorderButton`}
      ref={recordButtonRef}
    />
  );

  const onFileUpload = () => {
    handleAudioProcessingState({ state: true });
  };

  return (
    <AudioRecorderSelector
      setChatbarVisible={handleRecording}
      version={"inline"}
      buttonComponent={buttonComponent}
      onFileUpload={onFileUpload}
      disabled={audioState.isProcessing}
      showValue={false}
      showProgress={false}
      recordButtonRef={recordButtonRef}
      scrollContainerRef={scrollContainerRef}
      completeButtonSize={buttonSize}
      onBlobAttachmentRecieved={onBlobAttachmentRecieved}
      handleExternalUpdates={handleExternalUpdates}
      containerElement={containerElement}
      getButtonPositionRect={getButtonPositionRect}
      getVisualiserContainerRect={getVisualiserContainerRect}
    />
  );
};

AISpeechToText.propTypes = {
  uploadFile: PropTypes.func.isRequired,
  getSpeechToTextTranscript: PropTypes.func.isRequired,
  recorderRef: PropTypes.object.isRequired,
  handleAudioProcessingState: PropTypes.func.isRequired,
  updateUserPrompt: PropTypes.func.isRequired,
  handleShowAudioRecorder: PropTypes.func.isRequired,
  studentProgressReportId: PropTypes.string.isRequired,
  handleAudioRecordingState: PropTypes.func.isRequired,
  parentId: PropTypes.string,
  parentType: PropTypes.string,
  testId: PropTypes.string,
  recordButtonRef: PropTypes.object,
  scrollContainerRef: PropTypes.object,
  handleExternalUpdates: PropTypes.func,
  containerElement: PropTypes.object,
  getButtonPositionRect: PropTypes.func,
  getVisualiserContainerRect: PropTypes.func,
  buttonSize: PropTypes.oneOf([
    "xxx-small",
    "xx-small",
    "x-small",
    "small",
    "medium",
    "large",
  ]),
};

AISpeechToText.defaultProps = {
  parentId: "",
  parentType: "",
  testId: "AISpeechToText",
  recordButtonRef: null,
  scrollContainerRef: null,
  handleExternalUpdates: null,
  containerElement: null,
  getButtonPositionRect: null,
  getVisualiserContainerRect: null,
  buttonSize: "x-small",
};

const mapActionCreators = {
  uploadFile,
  getSpeechToTextTranscript,
};

export default connect(null, mapActionCreators)(AISpeechToText);
